import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, CircleDashed, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function VerificationQueue() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pdf-jobs/verification-queue");
        setData(data);
      } catch { toast.error("Errore caricamento coda di verifica"); }
    })();
  }, []);

  if (!data) return <div className="p-8 text-sm text-muted-foreground" data-testid="queue-loading">Caricamento coda…</div>;

  return (
    <div className="p-6 space-y-4" data-testid="verification-queue-page">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">Coda di verifica ground truth</h1>
        <Badge data-testid="queue-progress">{data.completed}/{data.total} casi completati</Badge>
      </div>
      <p className="text-xs text-muted-foreground max-w-3xl">
        Ogni caso richiede l'approvazione dei campi tecnici applicabili (o la classificazione MISSING / UNREADABLE / NOT_APPLICABLE).
        Le conferme creano nuove versioni della ground truth con audit trail: la versione storica non viene mai sovrascritta.
      </p>
      <Card>
        <CardHeader className="py-2"><CardTitle className="text-sm">Casi ({data.total})</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1">#</th><th>Caso</th><th>Approvati</th><th>Da confermare</th>
                <th>Campi mancanti</th><th>Conflitti</th><th>Feature ambigue</th><th>Distinta</th><th>Stato</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.queue.map((c) => (
                <tr key={c.job_id} className="border-b border-border hover:bg-muted/40" data-testid={`queue-row-${c.case_id}`}>
                  <td className="py-1.5">{(c.queue_order ?? 0) + 1}</td>
                  <td className="font-mono">{c.case_id}</td>
                  <td data-testid={`queue-handled-${c.case_id}`}>{c.indicators.handled}/{c.indicators.required_total}</td>
                  <td>{c.indicators.to_confirm.length}</td>
                  <td>{c.indicators.missing_fields.join(", ") || "—"}</td>
                  <td>{c.indicators.conflicts}</td>
                  <td>{c.indicators.ambiguous_features}</td>
                  <td>{c.indicators.gt_components > 1 ? `${c.indicators.gt_components} comp.` : "—"}</td>
                  <td>
                    {c.indicators.completed
                      ? <Badge className="bg-emerald-100 text-emerald-800" data-testid={`queue-completed-${c.case_id}`}><CheckCircle2 size={11} className="mr-1" />completato</Badge>
                      : <Badge className="bg-amber-100 text-amber-800"><CircleDashed size={11} className="mr-1" />da verificare</Badge>}
                  </td>
                  <td>
                    <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`queue-open-${c.case_id}`}
                      onClick={() => navigate(`/pdf-jobs/${c.job_id}/verify`)}>
                      Apri <ArrowRight size={11} className="ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
